/**
 * Global fetch interceptor: attaches the current session JWT as
 * `Authorization: Bearer …` on every request that targets the backend API
 * (`/api/*` on `API_BASE`). Also logs the user out if the server returns 401.
 *
 * Kept as a monkey-patch because the frontend has ~20 direct `fetch(...)`
 * call sites across editors and stores — updating each one would be noisy
 * and easy to regress. This single install runs before React mounts (see
 * `index.tsx`) so any subsequent fetch (in the client wrapper or ad-hoc)
 * inherits the header.
 *
 * Non-API URLs (audio blobs, external services) pass through untouched.
 */

import { API_BASE } from './apiBase';
import { useAuthStore } from '../store/authStore';

const NATIVE_FETCH = window.fetch.bind(window);

function urlOf(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.toString();
  return input.url;
}

function isApi(url: string): boolean {
  // API_BASE may be '' (same-origin) or a fully-qualified prod URL. Match
  // either shape and only intercept the /api/* subtree so unrelated fetches
  // (audio blobs, iTunes cover art, etc.) go through untouched.
  if (API_BASE && url.startsWith(API_BASE)) {
    return url.slice(API_BASE.length).startsWith('/api/');
  }
  return url.startsWith('/api/');
}

function attach(init: RequestInit | undefined, token: string): RequestInit {
  const headers = new Headers(init?.headers);
  if (!headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  return { ...init, headers };
}

export function installFetchInterceptor(): void {
  if ((window as any).__synapseFetchPatched) return;
  (window as any).__synapseFetchPatched = true;

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = urlOf(input);
    if (!isApi(url)) return NATIVE_FETCH(input, init);

    const token = useAuthStore.getState().token;
    const finalInit = token ? attach(init, token) : init;
    const res = await NATIVE_FETCH(input, finalInit);

    if (res.status === 401) {
      // Session expired or was never valid. Clear it so LoginGate re-mounts;
      // callers that care about the error still get the Response back.
      const auth = useAuthStore.getState();
      if (auth.mapper) auth.logout();
    }
    return res;
  };
}
